<template>
  <div class="homepage-container">
    <!-- Hero Banner (Dark Tech Background) -->
    <div class="hero-banner-container" :style="{ '--hero-bg-desktop': 'url(/assets/images/rewardstorechoicebanner.png)', '--hero-bg-mobile': 'url(/assets/images/rewardstorechoicebanner-mb.jpg)' }">
      <!-- Foreground Banner Images for perfect aspect ratio scaling -->
      <img src="/assets/images/rewardstorechoicebanner.png" alt="Hero Banner" class="d-none d-md-block w-100 hero-banner-img" />
      <img src="/assets/images/rewardstorechoicebanner-mb.jpg" alt="Hero Banner Mobile" class="d-md-none w-100 hero-banner-img" />

      <!-- Overlaid Details Card Container -->
      <div class="card-overlap-container">
        <div class="container d-flex justify-content-center">
          <!-- Overlay Card -->
          <div class="banner-gift-card">
            <div class="row align-items-center g-4">
              <!-- Left Side Content -->
              <div class="col-lg-7 col-md-12 text-start card-content-col">
                <h1 class="promo-main-heading mb-4" v-html="$t('Home.V2BannerTitle')"></h1>
                <p class="promo-main-desc mb-4" v-html="$t('Home.V2BannerDesc')"></p>
                
                <!-- Graphic element ONLY visible on mobile, placed between Description and Button to match mockup layout -->
                <img src="/assets/images/bannergiftsection.png" alt="Prizes Gift Box" class="img-fluid gift-banner-img mx-auto d-block d-lg-none mb-4" />
                
                <div class="btn-container mb-3">
                  <router-link to="/SignIn" class="btn btn-get-started">
                    {{ $t('Home.GetStartedBtn') }}
                  </router-link>
                </div>
                <p class="click-submit-text mb-4" v-html="$t('Home.ReadyToSubmitDesc')"></p>
                
                <div class="helper-links-container mt-3">
                  <p class="mb-1">
                    <span v-html="$t('Home.ViewParticipating')"></span>
                  </p>
                  <p class="mb-0 trouble-text">
                    {{ $t('Home.TroubleParticipating') }} <router-link to="/FAQ" class="faq-link">{{ $t('Home.FAQsText') }}</router-link>
                  </p>
                </div>
              </div>
              
              <!-- Right Side Graphic (ONLY visible on desktop) -->
              <div class="col-lg-5 col-md-12 text-center card-img-col d-none d-lg-block">
                <img src="/assets/images/bannergiftsection.png" alt="Prizes Gift Box" class="img-fluid gift-banner-img" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pattern Background Section (clean space with repeating triangle grid pattern) -->
    <div class="pattern-background-section" :style="{ '--pattern-bg': 'url(/assets/images/bgclaimoffer.png)' }"></div>

    <!-- How to Participate Section with vertical timeline list -->
    <section class="how-to-participate-section" :style="{ '--htw-bg-desktop': 'url(/assets/images/htpbgimg.jpg)', '--htw-bg-mobile': 'url(/assets/images/htpbgimg.jpg)' }">
      <div class="container py-3">
        <div class="row align-items-stretch">
          <!-- Left Column: Vertical Timeline Checklist -->
          <div class="col-lg-7 col-md-8 col-12 text-start text-white position-relative timeline-col">
            <h2 class="htw-heading mb-5 text-white">{{ $t('Home.HtwTitleDesktop') }}</h2>
            
            <div class="timeline-container position-relative">
              <!-- Step 1 -->
              <div class="timeline-item">
                <span class="step-num">01</span>
                <div class="step-icon-bg mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-shopping-cart">
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  </svg>
                </div>
                <h3 class="step-title text-white">{{ $t('Home.HtwV1Step1Title') }}</h3>
                <p class="step-desc text-light-grey">{{ $t('Home.HtwV1Step1Desc') }}</p>
              </div>

              <!-- Step 2 -->
              <div class="timeline-item">
                <span class="step-num">02</span>
                <div class="step-icon-bg mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-camera">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                    <circle cx="12" cy="13" r="4"></circle>
                  </svg>
                </div>
                <h3 class="step-title text-white">{{ $t('Home.HtwV1Step2Title') }}</h3>
                <p class="step-desc text-light-grey">{{ $t('Home.HtwV1Step2Desc') }}</p>
              </div>

              <!-- Step 3 -->
              <div class="timeline-item">
                <span class="step-num">03</span>
                <div class="step-icon-bg mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-upload">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                </div>
                <h3 class="step-title text-white">{{ $t('Home.HtwV1Step3Title') }}</h3>
                <p class="step-desc text-light-grey">{{ $t('Home.HtwV1Step3Desc') }}</p>
              </div>

              <!-- Step 4 -->
              <div class="timeline-item">
                <span class="step-num">04</span>
                <div class="step-icon-bg mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-shield">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                    <polyline points="9 11 11 13 15 9"></polyline>
                  </svg>
                </div>
                <h3 class="step-title text-white">{{ $t('Home.HtwV1Step4Title') }}</h3>
                <p class="step-desc text-light-grey">{{ $t('Home.HtwV1Step4Desc') }}</p>
              </div>
            </div>
          </div>

          <!-- Right Column: Kept empty to let the background dart board image show cleanly -->
          <div class="col-lg-5 col-md-4 col-12 d-none d-md-block empty-right-col"></div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'HomePage',
};
</script>

<style scoped>
.homepage-container {
  overflow-x: hidden;
  background-color: #ffffff;
}

/* Hero Banner Container (top dark portion) */
.hero-banner-container {
  width: 100%;
  position: relative;
  background-image: var(--hero-bg-desktop);
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
}

.hero-banner-img {
  width: 100%;
  height: auto;
  display: block;
}

/* Overlap container pulling the white card upward */
.card-overlap-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transform: translateY(50%);
  z-index: 10;
  width: 100%;
  padding: 0 15px;
}

/* White Details card overlay */
.banner-gift-card {
  background: #ffffff;
  border-radius: 28px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 15px 45px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
}

.promo-main-heading {
  font-size: 2.85rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  font-family: 'Outfit', sans-serif;
}

:deep(.text-primary) {
  color: #2584C5 !important;
}

.promo-main-desc {
  font-size: 1rem;
  color: #555555;
  line-height: 1.65;
}

.btn-container {
  display: flex;
  justify-content: flex-start;
}

.btn-get-started {
  display: inline-block;
  background-color: #2584C5;
  color: #ffffff !important;
  font-size: 1.15rem;
  font-weight: 600;
  padding: 0.8rem 5rem;
  border-radius: 6px;
  text-decoration: none;
  box-shadow: 0 4px 12px rgba(37, 132, 197, 0.25);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: none;
  text-align: center;
}

.btn-get-started:hover {
  background-color: #1b6ca2;
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(37, 132, 197, 0.4);
  text-decoration: none !important;
}

.btn-get-started:active {
  transform: translateY(0);
}

.click-submit-text {
  font-size: 0.95rem;
  color: #666666;
}

.helper-links-container {
  font-size: 0.95rem;
  color: #666666;
}

:deep(.helper-link) {
  color: #2584C5 !important;
  text-decoration: underline !important;
  font-weight: 500;
}

:deep(.helper-link:hover) {
  color: #1b6ca2 !important;
}

.trouble-text {
  font-weight: 400;
}

.faq-link {
  color: #2584C5 !important;
  text-decoration: underline !important;
  font-weight: 500;
}

.faq-link:hover {
  color: #1b6ca2 !important;
}

.gift-banner-img {
  max-width: 100%;
  height: auto;
  max-height: 320px;
  object-fit: contain;
}

/* Repeating Triangle Grid Pattern Bar underneath the card */
.pattern-background-section {
  background-image: var(--pattern-bg);
  background-repeat: repeat;
  background-position: top center;
  height: 240px;
  width: 100%;
  background-color: #ffffff;
  border-bottom: 1px solid #eaeef2;
  padding-top: 180px; /* added space to clear the overlapping card */
}

/* How to Participate Section with vertical timeline */
.how-to-participate-section {
  background-image: var(--htw-bg-desktop);
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  padding: 6rem 0;
  position: relative;
}

.htw-heading {
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  font-family: 'Outfit', sans-serif;
}

/* Vertical Timeline list */
.timeline-container {
  position: relative;
  padding-left: 0;
}

.timeline-item {
  position: relative;
  padding-left: 56px;
  padding-bottom: 2.75rem;
  z-index: 2;
}

/* Vertical Connecting Line segments using pseudo-elements */
.timeline-item::after {
  content: '';
  position: absolute;
  top: 48px; /* starts at the bottom edge of the step number block (48px height) */
  bottom: 0; /* goes down to the bottom of the item */
  left: 18px; /* centered horizontally under the 36px wide number */
  width: 1px;
  background: rgba(255, 255, 255, 0.25);
  z-index: 1;
}

.timeline-item:last-child::after {
  display: none; /* hide line extending below the last badge */
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.step-num {
  position: absolute;
  left: 0;
  top: 0;
  width: 36px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  font-family: 'Outfit', 'Inter', sans-serif;
  line-height: 48px; /* center it vertically with the 48px high icon box */
}

.step-icon-bg {
  background-color: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.4);
  width: 48px;
  height: 48px;
  border-radius: 10px; /* rounded corners exactly like the mockup */
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.step-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
  font-family: 'Outfit', sans-serif;
}

.step-desc {
  font-size: 0.95rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 0;
}

/* Hover effects for timelines */
.timeline-item:hover .step-icon-bg {
  transform: scale(1.05);
  background-color: rgba(255, 255, 255, 0.15);
  border-color: #ffffff;
}

.timeline-item:hover .step-title {
  color: #5ea7db;
}

/* Responsive Overrides */
@media screen and (max-width: 1199px) {
  .promo-main-heading {
    font-size: 2.35rem;
  }
}

@media screen and (max-width: 991px) {
  .hero-banner-container {
    height: 320px;
    background-image: var(--hero-bg-desktop);
    background-size: cover;
    background-position: center top;
    background-repeat: no-repeat;
  }
  .card-overlap-container {
    position: absolute;
    bottom: 0;
    transform: translateY(70%);
  }
  .pattern-background-section {
    height: 350px;
    padding-top: 100px;
  }
  .banner-gift-card {
    padding: 2.5rem;
  }
  .how-to-participate-section {
    padding: 4.5rem 0;
  }
  .htw-heading {
    font-size: 2.15rem;
    margin-bottom: 3rem;
  }
  .timeline-col {
    max-width: 100%;
    width: 100%;
  }
}

@media screen and (max-width: 767px) {
  .hero-banner-container {
    height: auto !important;
    background-image: var(--hero-bg-mobile) !important;
    background-size: cover;
    background-position: center top;
    background-repeat: no-repeat;
  }
  .pattern-background-section {
    display: block !important;
    background-image: var(--pattern-bg);
    background-repeat: repeat;
    background-position: top center;
    height: 620px !important;
    width: 100%;
    background-color: #ffffff;
    border-bottom: 1px solid #eaeef2;
    padding-top: 0 !important;
  }
  .banner-gift-card {
    padding: 2.5rem 1.5rem;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    background: #ffffff;
  }
  .promo-main-heading {
    font-size: 1.85rem;
    text-align: center;
  }
  .promo-main-desc {
    font-size: 1rem;
    text-align: center;
    line-height: 1.55;
  }
  .btn-container {
    justify-content: center;
  }
  .btn-get-started {
    width: 100%;
    max-width: 280px;
    padding: 0.75rem 2rem;
  }
  .click-submit-text {
    text-align: center;
  }
  .helper-links-container {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
  }
  .gift-banner-img {
    max-height: 220px;
  }
  .how-to-participate-section {
    background-image: var(--htw-bg-mobile);
    padding: 3.5rem 0;
  }
  .htw-heading {
    font-size: 1.85rem;
    margin-bottom: 2.5rem;
    text-align: center;
  }
  
  /* Timeline mobile overrides to preserve the exact layout and scale down for smaller screens */
  .timeline-item::after {
    left: 16px !important;
    top: 40px;
  }
  .timeline-item {
    padding-left: 52px;
    padding-bottom: 2rem;
  }
  .step-num {
    width: 32px;
    line-height: 40px;
    font-size: 1.35rem;
  }
  .step-icon-bg {
    width: 40px;
    height: 40px;
  }
  .step-title {
    font-size: 1.25rem;
    margin-top: 0.5rem;
  }
}
</style>
